/**
 * Batch-2 pieces drawn the same way on the office and field screens: the
 * welder-qualification badge, a refusal, the shot guard's warnings, and the
 * repair-rate card.
 *
 * THE QUALIFICATION CHECK WARNS AND NEVER BLOCKS (fieldiq-api#1144): the welder
 * is often the customer's or a contractor's hand, so nothing here disables a
 * save on it. The reasons are the API's codes, put into words.
 *
 * NOTHING HERE COMPUTES. The repair rate, its small-sample flag, a weld's
 * standing and the qualification verdict are all the API's.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNdtRepairRates } from '../../hooks/useNdtInspection';
import { queryLoadFailure } from '../../lib/load-failure';
import { formatRepairRate, ndtQualReasonText, ndtQualStatusSkin, type NdtRefusal } from '../../lib/ndt-inspection';
import type { NdtRepairRate } from '../../types/ndt-inspection';
import { NdtBadge, NdtPanel } from './NdtBits';

/** The qualification verdict as a badge, and (unless `compact`) its reasons as plain words. */
export function NdtQualBadge({
  status,
  reasons,
  compact = false,
}: {
  status: string | null | undefined;
  reasons?: string[] | null;
  compact?: boolean;
}) {
  const skin = ndtQualStatusSkin(status);
  const list = status === 'not_qualified' ? reasons ?? [] : [];
  return (
    <div data-testid="ndt-qual-badge" className="space-y-1">
      <NdtBadge skin={skin} value={status} />
      {!compact && list.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-fieldiq-text">
          {list.map((r) => (
            <li key={r}>{ndtQualReasonText(r)}</li>
          ))}
        </ul>
      )}
      {!compact && status === 'not_qualified' && (
        <p className="text-xs text-fieldiq-muted">A warning only — the weld is still recorded.</p>
      )}
    </div>
  );
}

/** A write the API refused: the headline sentence, every shot-guard failure, and any warnings beside it. */
export function NdtRefusalNotice({ refusal }: { refusal: NdtRefusal | null }) {
  if (!refusal) return null;
  const extra = refusal.failures.filter((f) => f !== refusal.message);
  return (
    <div role="alert" data-testid="ndt-refusal" className="space-y-2 rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      <p className="font-semibold">{refusal.message}</p>
      {extra.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5" aria-label="Why it was refused">
          {extra.map((f, n) => (
            <li key={n}>{f}</li>
          ))}
        </ul>
      )}
      {refusal.warnings.length > 0 && <NdtWarnings warnings={refusal.warnings} title="Also due soon" />}
    </div>
  );
}

/** The shot guard's warnings — things due soon, never a refusal. */
export function NdtWarnings({ warnings, title = 'Saved, with warnings' }: { warnings: string[]; title?: string }) {
  if (warnings.length === 0) return null;
  return (
    <div data-testid="ndt-warnings" className="rounded-fi-control border border-fi-signal-amber/50 bg-fi-signal-amber/10 px-3 py-2 text-sm text-fieldiq-text">
      <p className="flex items-center gap-1.5 font-semibold">
        <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
        {title}
      </p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {warnings.map((w, n) => (
          <li key={n}>{w}</li>
        ))}
      </ul>
    </div>
  );
}

/** "12.5% · 1 of 8 welds" — or "—" with "No weld examined yet". A small sample says so. */
export function RepairRateFigure({ rate }: { rate: NdtRepairRate | null | undefined }) {
  if (!rate) return <span>—</span>;
  return (
    <span data-testid="ndt-repair-rate">
      <span className="font-mono font-extrabold text-white">{formatRepairRate(rate.rate)}</span>
      <span className="ml-2 text-xs text-fieldiq-muted">
        {rate.examined === 0 ? 'No weld examined yet' : `${rate.repaired} of ${rate.examined} weld${rate.examined === 1 ? '' : 's'} repaired`}
        {rate.examined > 0 && rate.smallSample && ' · small sample'}
      </span>
    </span>
  );
}

/**
 * Repair rate for one job or one welder, from GET /repair-rates. The figure is
 * the API's (original-cycle exams only, attributed to the original welder).
 */
export function NdtRepairRateCard({ jobId, welderId }: { jobId?: string; welderId?: string }) {
  const query = useNdtRepairRates(jobId ? { jobId } : { welderId });
  const failure = queryLoadFailure(query);
  const overall = query.data?.data?.overall ?? null;
  return (
    <NdtPanel eyebrow="Quality" title="Repair rate" testId="ndt-repair-rate-card">
      <div className="px-4 py-4 text-sm text-fieldiq-text">
        {query.isLoading ? (
          <span className="text-fieldiq-muted">Loading…</span>
        ) : failure ? (
          <span>The repair rate could not be loaded{failure === 'refused' ? ' — you do not have access.' : '. Try again shortly.'}</span>
        ) : (
          <>
            <RepairRateFigure rate={overall} />
            <p className="mt-2 text-xs text-fieldiq-muted">Welds repaired or cut out on their first exam, out of those examined.</p>
          </>
        )}
      </div>
    </NdtPanel>
  );
}
